import { useState } from 'react';
import escochexLogo from '../../../assets/escochex-logo.webp';
import '../admin-signup.css';
import { ThemeToggle } from '../../../shared/theme/ThemeToggle';
import { PARTNER_PRIMARY_STEPS, PARTNER_WIZARD_STEPS, partnerPrimaryStepFor, type PartnerWizardStepKey } from '../types';
import { PartnerAccountSetupStep } from './steps/PartnerAccountSetupStep';
import { PartnerInfoStep } from './steps/PartnerInfoStep';
import { IdentityVerificationStep } from './steps/IdentityVerificationStep';
import { AddressStep } from './steps/AddressStep';
import { BankingStep } from './steps/BankingStep';
import { AdminInfoStep } from './steps/AdminInfoStep';
import { WelcomeStep } from './steps/WelcomeStep';

/**
 * Partner Sign Up — EPN-35. Mirrors AdminSignUpPage's own two-column `.setup-shell` layout and local-state
 * step model exactly (see that file's doc comment for why step position lives in component state, not the
 * URL). The only genuinely new screens are Account Setup and Partner Information; every other screen is the
 * identical Admin Sign Up component, reused via the small optional props each one now accepts.
 * <p>
 * Account Setup collects the registering Owner's email/password BEFORE any backend call exists to attach
 * them to (the approved Partner design puts this first, opposite of Admin Sign Up's own ordering) — this
 * page holds those two values in state and threads them into the later, reused AdminInfoStep as
 * `initialOwnerEmail`/`initialOwnerPassword`, so the actual `createOwner` call still happens at the exact
 * same point in the pipeline Admin Sign Up already uses. No new owner-creation code path exists anywhere in
 * this flow.
 */
export function PartnerSignUpPage() {
  const [step, setStep] = useState<PartnerWizardStepKey>('account-setup');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');

  const stepIndex = PARTNER_WIZARD_STEPS.findIndex((s) => s.key === step);
  const goNext = () => {
    const next = PARTNER_WIZARD_STEPS[stepIndex + 1];
    if (!next) return;
    setStep(next.key);
    // The plaintext password only needs to survive long enough to prefill the reused AdminInfoStep (see
    // this component's own doc comment) — once the wizard reaches the terminal Welcome screen, nothing
    // reads it again, so it is cleared here rather than left sitting in memory indefinitely for as long as
    // the tab stays open. Email is left alone — it is not a credential.
    if (next.key === 'welcome') {
      setOwnerPassword('');
    }
  };
  const goBack = () => {
    const previous = PARTNER_WIZARD_STEPS[stepIndex - 1];
    if (previous) setStep(previous.key);
  };

  const primaryStep = partnerPrimaryStepFor(step);
  const primaryIndex = PARTNER_PRIMARY_STEPS.findIndex((s) => s.key === primaryStep);
  const isWelcome = step === 'welcome';

  function renderStep() {
    switch (step) {
      case 'account-setup':
        return (
          <PartnerAccountSetupStep
            initialEmail={ownerEmail}
            initialPassword={ownerPassword}
            onContinue={(email, password) => {
              setOwnerEmail(email);
              setOwnerPassword(password);
              goNext();
            }}
          />
        );
      case 'partner-info':
        return <PartnerInfoStep goNext={goNext} goBack={goBack} />;
      case 'identity-verification':
        return (
          <IdentityVerificationStep
            goNext={goNext}
            goBack={goBack}
            heading="Step 2: Partner Information"
            accessibleHeading="Step 2: Partner Information — Identity Verification"
            firstSubstepLabel="Partner Info"
          />
        );
      case 'address':
        return <AddressStep goNext={goNext} goBack={goBack} heading="Step 3: Company Address" />;
      case 'banking':
        return <BankingStep goNext={goNext} goBack={goBack} heading="Step 4: Banking Details and Payroll Setup" />;
      case 'team-members':
        return (
          <AdminInfoStep
            goNext={goNext}
            goBack={goBack}
            heading="Step 5: Team Members"
            memberNoun="Team Member"
            memberNounPluralLower="team members"
            tabListAriaLabel="Team Members"
            initialOwnerEmail={ownerEmail}
            initialOwnerPassword={ownerPassword}
          />
        );
      case 'welcome':
        return (
          <WelcomeStep
            goNext={goNext}
            goBack={goBack}
            bodyCopy="Your partner account has been successfully created. Head over to the sign-in page and log in with your credentials to get started."
          />
        );
    }
  }

  return (
    <div className="admin-signup-shell" data-testid="partner-signup-page">
      <ThemeToggle />
      <section className="setup-shell">
        <aside className="left-panel">
          <div className="brand">
            <img src={escochexLogo} alt="EscoChex — A Payroll Software Company" />
          </div>
          <h1 className="setup-title">Partner Account Setup</h1>
          <div className="main-steps" aria-label="Partner sign up steps">
            {PARTNER_PRIMARY_STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`main-step${i === primaryIndex && !isWelcome ? ' active' : ''}${i < primaryIndex || isWelcome ? ' done' : ''}`}
                aria-current={i === primaryIndex && !isWelcome ? 'step' : undefined}
              >
                <div className="circle">
                  <span>{i + 1}</span>
                </div>
                <div className="copy">
                  <strong>{s.label}</strong>
                  <span>{s.description}</span>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <section className="form-panel">
          <div className="form-panel-inner">{renderStep()}</div>
        </section>
      </section>
    </div>
  );
}
